import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TypewriterText } from '../hooks/useTypewriter';
import { bytes } from '../components/ModelSpecs';

// What your models read. Links are looked at first (what is it, how big, what licence),
// then read once on logOS's side and kept as text for your runs; uploads likewise.
// A hugging face dataset is streamed during training instead. Everything is private.

const API = '/api';

function headers(json = true) {
  const h = json ? { 'Content-Type': 'application/json' } : {};
  const t = localStorage.getItem('logos_session_token');
  if (t) h['Authorization'] = `Bearer ${t}`;
  return h;
}

const KIND = { huggingface: 'hugging face', github: 'github', gutenberg: 'gutenberg', wikipedia: 'wikipedia',
  arxiv: 'arxiv', file: 'file', page: 'page', upload: 'upload' };
const STATUS_TONE = { ready: 'text-white border-white/40', preparing: 'text-[#bbbbbb] border-[#4a514a]', failed: 'text-[#ff8888] border-[#663333]' };

function put(url, file, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new Error(`upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('upload failed'));
    xhr.timeout = 0;
    xhr.send(file);
  });
}

function Heading({ children, note }) {
  return (
    <div className="flex items-baseline gap-3 mb-2">
      <h2 className="font-mono text-[11px] text-[#666666] lowercase tracking-wide">{children}</h2>
      {note && <span className="ml-auto font-mono text-[11px] text-[#555555] lowercase">{note}</span>}
    </div>
  );
}

function Select({ value, options, onChange }) {
  return (
    <select value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="bg-transparent border border-[#383f38] rounded px-1.5 py-0.5 font-mono text-xs text-white outline-none focus:border-white">
      {options.map((o) => <option key={o} value={o} className="bg-black">{o}</option>)}
    </select>
  );
}

function QueueRow({ entry, onChange, onRemove }) {
  const item = entry.item;
  return (
    <div className="py-3 space-y-1.5" data-testid="queued-item">
      <div className="flex items-baseline gap-3">
        <span className="font-mono text-[10px] text-[#777777] lowercase shrink-0 w-20">{item ? KIND[item.kind] : entry.kind || 'link'}</span>
        <span className="font-mono text-sm text-white lowercase truncate">{item?.title || entry.label}</span>
        <button onClick={onRemove} className="ml-auto font-mono text-xs text-[#666666] hover:text-white shrink-0" aria-label="remove">×</button>
      </div>
      <div className="pl-[5.75rem] font-mono text-[11px] lowercase space-y-1">
        {entry.state === 'looking' && <p className="text-[#888888]">looking…</p>}
        {entry.state === 'uploading' && <p className="text-[#888888]">uploading {Math.round((entry.progress || 0) * 100)}%</p>}
        {entry.error && <p className="text-[#ff8888]">{entry.error}</p>}
        {item && (
          <>
            <p className="text-[#888888]">
              {item.bytes ? bytes(item.bytes) : 'size unknown'} · {item.licence ? `licence: ${item.licence}` : 'licence unknown'}
              {item.note ? ` · ${item.note}` : ''}
            </p>
            {item.kind === 'huggingface' && (
              <div className="flex flex-wrap gap-3 items-center text-[#888888]">
                <label className="flex gap-1.5 items-center">subset <Select value={item.config} options={Object.keys(item.configs || {})}
                  onChange={(config) => onChange({ ...item, config, split: Object.keys(item.configs[config])[0] })} /></label>
                <label className="flex gap-1.5 items-center">split <Select value={item.split} options={Object.keys((item.configs || {})[item.config] || {})}
                  onChange={(split) => onChange({ ...item, split, bytes: item.configs[item.config][split].bytes })} /></label>
                {item.columns?.length > 0 && (
                  <label className="flex gap-1.5 items-center">text column <Select value={item.column} options={item.columns}
                    onChange={(column) => onChange({ ...item, column })} /></label>
                )}
              </div>
            )}
            {item.kind === 'page' && item.attachments?.length > 0 && (
              <div className="space-y-1">
                <label className="flex gap-2 items-center text-[#aaaaaa]">
                  <input type="checkbox" checked={item.include_page !== false} onChange={(e) => onChange({ ...item, include_page: e.target.checked })} />
                  the page itself
                </label>
                {item.attachments.map((a) => (
                  <label key={a.url} className="flex gap-2 items-center text-[#aaaaaa]">
                    <input type="checkbox" checked={(item.chosen || []).includes(a.url)}
                      onChange={(e) => onChange({ ...item, chosen: e.target.checked ? [...(item.chosen || []), a.url] : (item.chosen || []).filter((u) => u !== a.url) })} />
                    <span className="truncate">{a.name}</span><span className="text-[#555555] shrink-0">{a.bytes ? bytes(a.bytes) : ''}</span>
                  </label>
                ))}
              </div>
            )}
            {item.kind === 'wikipedia' && item.articles && <p className="text-[#666666]">{item.articles.length} articles</p>}
          </>
        )}
      </div>
    </div>
  );
}

function SourceRow({ source, onChanged }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!open) return;
    fetch(`${API}/data/sources/${source.source_id}`, { headers: headers(), credentials: 'include' })
      .then((r) => r.json()).then(setDetail);
  }, [open, source.status, source.source_id]);
  const remove = async () => {
    if (!window.confirm(`delete "${source.label}" and its stored text? this can't be undone.`)) return;
    const res = await fetch(`${API}/data/sources/${source.source_id}`, { method: 'DELETE', headers: headers(), credentials: 'include' });
    if (!res.ok) window.alert((await res.json().catch(() => ({}))).detail || 'could not delete');
    onChanged();
  };
  const retry = async () => {
    await fetch(`${API}/data/sources/${source.source_id}/retry`, { method: 'POST', headers: headers(), credentials: 'include' });
    onChanged();
  };
  return (
    <div className="py-3" data-testid="source-row">
      <button onClick={() => setOpen(!open)} className="w-full text-left flex items-baseline gap-3 flex-wrap">
        <span className="font-mono text-sm text-white lowercase">{source.label}</span>
        <span className={`font-mono text-[10px] lowercase px-2 py-0.5 rounded border ${STATUS_TONE[source.status]}`}>
          {source.streamed ? 'streamed' : source.status}
        </span>
        <span className="ml-auto font-mono text-xs text-[#888888]">
          {source.bytes ? bytes(source.bytes) : ''}{source.documents ? ` · ${source.documents.toLocaleString()} docs` : ''}
        </span>
      </button>
      <p className="font-mono text-[11px] text-[#666666] lowercase mt-1">
        {source.kinds.map((k) => KIND[k] || k).join(', ')} · {source.licence ? `licence: ${source.licence}` : 'licence not given'}
        {source.error ? ` · ${source.error}` : ''}
      </p>
      {open && detail && (
        <div className="mt-3 space-y-3 font-mono text-[11px] lowercase">
          {detail.log?.length > 0 && (
            <div className="space-y-0.5">
              {detail.log.map((l, i) => (
                <p key={i} className={l.error ? 'text-[#ff8888]' : 'text-[#888888]'}>
                  {l.title} · {l.error ? l.error : `${l.documents} docs, ${bytes(l.bytes)}`}
                </p>
              ))}
            </div>
          )}
          {detail.sample && (
            <pre className="whitespace-pre-wrap normal-case text-[#aaaaaa] border-l border-[#2a312b] pl-3 max-h-48 overflow-y-auto">{detail.sample}</pre>
          )}
          <div className="flex gap-4">
            {source.status === 'failed' && !source.streamed && <button onClick={retry} className="text-[#aaaaaa] hover:text-white">prepare again</button>}
            <button onClick={remove} className="text-[#888888] hover:text-[#ff8888]">delete</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Data() {
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [link, setLink] = useState('');
  const [queue, setQueue] = useState([]);
  const [label, setLabel] = useState('');
  const [licence, setLicence] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileInput = useRef(null);
  const nextId = useRef(0);

  const load = useCallback(() => fetch(`${API}/data`, { headers: headers(), credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null)).then(setData), []);
  useEffect(() => { if (user) load(); }, [user, load]);
  const preparing = data?.sources?.some((s) => s.status === 'preparing');
  useEffect(() => {
    if (!preparing) return undefined;
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [preparing, load]);

  const update = (id, change) => setQueue((q) => q.map((e) => (e.id === id ? { ...e, ...change } : e)));

  const addLink = async () => {
    const url = link.trim();
    if (!url) return;
    const id = nextId.current++;
    setLink('');
    setQueue((q) => [...q, { id, label: url, state: 'looking' }]);
    const res = await fetch(`${API}/data/resolve`, { method: 'POST', headers: headers(), credentials: 'include', body: JSON.stringify({ url }) });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) update(id, { state: 'error', error: body.detail || 'could not read that link' });
    else update(id, { state: 'ready', item: { ...body, include_page: true, chosen: [] } });
  };

  const addFiles = async (files) => {
    for (const file of Array.from(files)) {
      const id = nextId.current++;
      setQueue((q) => [...q, { id, label: file.name, kind: 'upload', state: 'uploading', progress: 0 }]);
      const res = await fetch(`${API}/data/uploads`, { method: 'POST', headers: headers(), credentials: 'include',
        body: JSON.stringify({ name: file.name, bytes: file.size }) });
      const slot = await res.json().catch(() => ({}));
      if (!res.ok) { update(id, { state: 'error', error: slot.detail || 'could not upload' }); continue; }
      try {
        await put(slot.upload_url, file, (progress) => update(id, { progress }));
        update(id, { state: 'ready', item: { kind: 'upload', r2_key: slot.r2_key, name: slot.name, title: slot.name, bytes: file.size } });
      } catch (e) {
        update(id, { state: 'error', error: e.message });
      }
    }
  };

  const ready = queue.filter((e) => e.state === 'ready');
  const submit = async () => {
    setBusy(true); setError(null);
    const res = await fetch(`${API}/data/sources`, { method: 'POST', headers: headers(), credentials: 'include',
      body: JSON.stringify({ label, licence, items: ready.map((e) => e.item) }) });
    const body = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setError(body.detail || 'could not add those'); return; }
    setQueue((q) => q.filter((e) => e.state !== 'ready'));
    setLabel(''); setLicence('');
    load();
  };

  if (loading) return <div className="min-h-screen bg-black" />;
  if (!user) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-[#888888] lowercase">sign in to add what your models read.</p>
        <button onClick={login} className="border border-[#383f38] px-5 py-2 rounded-full text-white lowercase hover:bg-white hover:text-black transition-all">login</button>
      </div>
    );
  }

  const quota = data?.quota;
  return (
    <div className="min-h-screen bg-black py-10 px-4 sm:px-6 lg:px-8 page-wrapper" data-testid="data-page">
      <div className="max-w-3xl mx-auto space-y-10">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold text-white lowercase font-mono"><TypewriterText text="data" speed={65} /></h1>
          <p className="text-[#aaaaaa] mt-3">
            what your models read. paste links or drop files; each is read once and kept as text for your runs.
            everything here is private. pick sources when you <Link to="/train" className="text-white hover:underline underline-offset-4 decoration-[#444444]">train</Link>.
          </p>
        </div>

        <section>
          <Heading note={quota ? `${bytes(quota.used_bytes)} of ${bytes(quota.limit_bytes)} used` : null}>add</Heading>
          <div className="flex gap-2">
            <input value={link} onChange={(e) => setLink(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addLink()}
              placeholder="a link: a page, a file, hugging face, github, gutenberg, wikipedia, arxiv"
              className="flex-1 min-w-0 bg-transparent border border-[#383f38] rounded-lg px-3 py-2 text-white font-mono text-sm outline-none focus:border-white placeholder:text-[#555555]"
              data-testid="link-input" />
            <button onClick={addLink} aria-label="add link"
              className="border border-[#383f38] rounded-lg px-4 text-white font-mono hover:bg-white hover:text-black transition-all" data-testid="add-link">+</button>
          </div>
          <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
            onClick={() => fileInput.current?.click()}
            className="mt-2 border border-dashed border-[#2a312b] rounded-lg py-4 text-center font-mono text-xs text-[#666666] lowercase cursor-pointer hover:border-[#4a514a] hover:text-[#aaaaaa]">
            or drop files here: text, markdown, csv, json, parquet, pdf, epub, docx, zip
            <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }} />
          </div>

          {queue.length > 0 && (
            <div className="mt-4">
              <div className="divide-y divide-[#1a201b] border-y border-[#1a201b]">
                {queue.map((e) => (
                  <QueueRow key={e.id} entry={e} onRemove={() => setQueue((q) => q.filter((x) => x.id !== e.id))}
                    onChange={(item) => update(e.id, { item })} />
                ))}
              </div>
              <div className="flex gap-2 flex-wrap mt-3 items-center">
                <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="name (optional)"
                  className="bg-transparent border border-[#383f38] rounded-lg px-3 py-1.5 text-white font-mono text-xs outline-none focus:border-white placeholder:text-[#555555] w-44" />
                <input value={licence} onChange={(e) => setLicence(e.target.value)} placeholder="licence (if you know)"
                  className="bg-transparent border border-[#383f38] rounded-lg px-3 py-1.5 text-white font-mono text-xs outline-none focus:border-white placeholder:text-[#555555] w-44" />
                <button onClick={submit} disabled={busy || ready.length === 0}
                  className="ml-auto border border-white bg-white text-black px-5 py-1.5 rounded-full font-mono text-xs lowercase hover:bg-transparent hover:text-white transition-all disabled:opacity-40"
                  data-testid="add-sources">
                  {busy ? 'adding…' : `add ${ready.length} to my data`}
                </button>
              </div>
              <p className="font-mono text-[11px] text-[#555555] lowercase mt-2">
                hugging face datasets are streamed when a run reads them; everything else in the list becomes one source,
                read once now. only add what you have the right to use.
              </p>
              {error && <p className="font-mono text-xs text-[#ff8888] lowercase mt-2">{error}</p>}
            </div>
          )}
        </section>

        <section>
          <Heading>your sources</Heading>
          {!data ? <p className="font-mono text-xs text-[#555555] lowercase">loading…</p> : data.sources.length === 0 ? (
            <p className="font-mono text-xs text-[#555555] lowercase py-3 border-y border-[#1a201b]">nothing yet</p>
          ) : (
            <div className="divide-y divide-[#1a201b] border-y border-[#1a201b]">
              {data.sources.map((s) => <SourceRow key={s.source_id} source={s} onChanged={load} />)}
            </div>
          )}
        </section>

        {data && (
          <section>
            <Heading>catalogue</Heading>
            <div className="divide-y divide-[#1a201b] border-y border-[#1a201b]">
              {data.catalogue.map((c) => (
                <div key={c.id} className="py-2.5">
                  <div className="flex items-baseline gap-3">
                    <a href={`https://huggingface.co/datasets/${c.dataset}`} target="_blank" rel="noreferrer"
                      className="font-mono text-sm text-white lowercase hover:underline underline-offset-4 decoration-[#444444]">{c.label}</a>
                    <span className="ml-auto font-mono text-xs text-[#888888]">{c.gigabytes} gb · {c.licence}</span>
                  </div>
                  <p className="font-mono text-[11px] text-[#666666] lowercase mt-0.5">{c.about}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
